import { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { NavLink, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { capitalize, noop } from 'lodash';
import { useBoolean } from 'usehooks-ts';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { Button, Popover } from '@ebsco-ui/ebsco-ui';

import { FeatureFlags, Icon } from '@app/components';
import { navigationMessages } from '@app/translations';
import { FeatureFlagToMatch, SetState } from '@app/constants';

import css from '../NavigationPanel.module.scss';

interface NavigationLink {
  href: string;
  label: string;
  flags?: FeatureFlagToMatch[];
}

interface NavigationDropdownProps {
  name: string;
  links: NavigationLink[];
  icon: IconDefinition;
  isMobile?: boolean;
  flags?: FeatureFlagToMatch[];
  setShowDrawer?: SetState<boolean>;
}

export const NavigationDropdown: FC<NavigationDropdownProps> = ({
  name,
  links,
  icon,
  flags,
  isMobile,
  setShowDrawer,
}) => {
  const [popoverWidth, setPopoverWidth] = useState<number>(0);
  const popoverRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { $t } = useIntl();
  const { value: isDropdownExpanded, toggle: toggleDropdown } =
    useBoolean(false);

  const handleCloseMobileMenu = () => setShowDrawer && setShowDrawer(false);
  const getDropdownLinksLabel = ({ name, isExpanded }) =>
    $t(
      navigationMessages[
        `${isExpanded ? 'collapse' : 'expand'}${capitalize(name)}`
      ]
    );

  const getDropdownLinks = (
    links: NavigationLink[] = [],
    handlePopoverClose = noop
  ) => (
    <ul className={css.dropdownLinks}>
      {links.map(({ href, label, flags }) => (
        <FeatureFlags key={label} flagsToMatch={flags}>
          <li>
            <NavLink
              to={href}
              className={({ isActive }) =>
                isActive ? css.isActive : undefined
              }
              onClick={() => {
                handleCloseMobileMenu();
                handlePopoverClose();
              }}
            >
              {label}
            </NavLink>
          </li>
        </FeatureFlags>
      ))}
    </ul>
  );

  const isActive = links.some(({ href }) => location.pathname.startsWith(href));

  const buttonContent = (
    <>
      <Icon icon={icon} className={css.icon} />
      <span className={css.content}>{$t(navigationMessages[name])}</span>
      <Icon
        icon={faChevronRight}
        className={cn(css.arrow, {
          [css.isRotate]: isMobile && isDropdownExpanded,
        })}
      />
    </>
  );

  useEffect(() => {
    if (!isMobile) {
      setPopoverWidth(popoverRef.current?.clientWidth ?? 0);
    }
  }, [isMobile]);

  return (
    <FeatureFlags flagsToMatch={flags} key={name}>
      <li
        {...(popoverWidth && {
          style: { width: `${popoverWidth}px` },
        })}
      >
        {isMobile ? (
          <>
            <Button
              className={cn(css.dropdown, css.link, {
                [css.isActive]: isActive,
              })}
              styleType="flat-icon"
              aria-label={getDropdownLinksLabel({
                name,
                isExpanded: isDropdownExpanded,
              })}
              onClick={toggleDropdown}
            >
              {buttonContent}
            </Button>
            {isDropdownExpanded && getDropdownLinks(links)}
          </>
        ) : (
          <Popover
            uniqueName={`${name}-links`}
            panelPlacement="bottom-start"
            className={css.wrapper}
          >
            <Popover.ContextConsumer>
              {({ isOpen, closePopover }) => (
                <>
                  <Popover.Button
                    id={`${name}-links-button`}
                    styleType="flat-icon"
                    className={cn(
                      css.dropdown,
                      css.link,
                      {
                        [css.isActive]: isActive,
                      },
                      'popoverButton'
                    )}
                    aria-label={getDropdownLinksLabel({
                      name,
                      isExpanded: isOpen,
                    })}
                  >
                    <div ref={popoverRef} className={css.label}>
                      {buttonContent}
                    </div>
                  </Popover.Button>
                  <Popover.Panel
                    aria-describedby={`${name}-links-button`}
                    className="popoverWrapper"
                  >
                    {getDropdownLinks(links, closePopover)}
                  </Popover.Panel>
                </>
              )}
            </Popover.ContextConsumer>
          </Popover>
        )}
      </li>
    </FeatureFlags>
  );
};
